import { Link } from 'react-router-dom';

import StatsSection from '../../../common/components/stats-section';
import TrendChart from '../../../common/components/trend-chart';
import { IconMilk, IconScale, IconSyringe } from '../../../common/icons';
import { DONOR_ECONOMIC_ROWS, LIQUIDATION_ROWS, PRODUCTION_CARDS, RATE_ROWS } from '../../../core/constants/kpis';
import { SG_FEATURES } from '../../../core/constants/sg';
import { envelopeData, envelopeModules } from '../../../core/network/envelope';
import { formatDateOnly, periodLabel } from '../../../core/utils/date';
import { fmtMoney, fmtNum, fmtPct } from '../../../core/utils/format';
import { useClientDonorDetail } from '../contexts/donor-detail';
import { useClientLiquidation } from '../contexts/liquidations';

import '../styles/detail.css';

// TSS vite/03 — dashboard de UNA donadora (cowhousing). Endpoint REAL:
// GET public/donors/:id (sobre view:"detail"; bloques en envelope.data).
// 404 = donadora no co-poseída / inexistente (anti-enumeración) → estado
// amable, sin romper. El backend es dueño de números y qualifiers: acá solo
// formato (fmtMoney/fmtNum/fmtPct) + mapeo key → label/color.
// Los indicadores los pinta el StatsSection compartido (common/, D-006).

const SEX_LABELS = { F: 'Hembra', M: 'Macho' };

function BackLink() {
    return <Link to="/mis-vacas" className="donora-detail__back">← Mis vacas</Link>;
}

function Field({ label, children }) {
    return (
        <div className="donora-detail__field">
            <span className="donora-detail__field-label">{label}</span>
            <span className="donora-detail__field-value">
                {children || <span className="donora-detail__muted">—</span>}
            </span>
        </div>
    );
}

// Valorización (Q-150, ?include=economics): compra → valor actual + crecimiento.
// El backend deriva growth_abs/growth_pct; el front solo formatea. Todo
// null-safe: sin valuación cargada por staff (purchase_value null) el bloque no
// se muestra. El signo del crecimiento es una POLARIDAD → color de estado
// reservado (success/danger) SIEMPRE acompañado de flecha + valor con signo
// (nunca color solo). current_estimated_value == gross_value.
function growthTone(pct) {
    if (pct == null || pct === 0) return 'flat';
    return pct > 0 ? 'up' : 'down';
}

function Valorization({ economics, currentValue }) {
    const purchase = economics?.purchase_value;
    if (purchase == null) return null; // sin valuación → nada que valorizar

    const current   = economics?.current_estimated_value ?? currentValue;
    const growthAbs = economics?.growth_abs;
    const growthPct = economics?.growth_pct;
    const tone  = growthTone(growthPct);
    const arrow = tone === 'up' ? '↑' : tone === 'down' ? '↓' : '→';
    const sign  = growthPct > 0 ? '+' : '';

    return (
        <section className="donora-detail__card">
            <h3 className="donora-detail__section-title">Valoración de tu inversión</h3>
            <div className="donora-detail__valz">
                <div className="donora-detail__valz-stat">
                    <span className="donora-detail__valz-label">Valor de compra</span>
                    <span className="donora-detail__valz-value">{fmtMoney(purchase)}</span>
                    {economics?.purchase_date && (
                        <span className="donora-detail__valz-sub">{formatDateOnly(economics.purchase_date)}</span>
                    )}
                </div>
                <div className="donora-detail__valz-stat">
                    <span className="donora-detail__valz-label">Valor actual estimado</span>
                    <span className="donora-detail__valz-value">{fmtMoney(current)}</span>
                </div>
                <div className={`donora-detail__valz-stat donora-detail__valz-stat--${tone}`}>
                    <span className="donora-detail__valz-label">Crecimiento</span>
                    <span className="donora-detail__valz-value">
                        <span className="donora-detail__valz-arrow" aria-hidden="true">{arrow}</span>
                        {growthPct != null ? `${sign}${fmtPct(growthPct)}` : '—'}
                    </span>
                    {growthAbs != null && (
                        <span className="donora-detail__valz-sub">{sign}{fmtMoney(growthAbs)}</span>
                    )}
                </div>
            </div>
        </section>
    );
}

// E1–E3 con fmtMoney; el percent acompaña al stake (kpi-catalog E2). El
// offspring_return del detalle vive en economics (dos superficies, misma
// semántica que el listado). Debajo, la Valorización (solo con ?include).
function EconomicsCard({ detail }) {
    const money = {
        gross_value:      detail.gross_value,
        stake_value:      detail.stake_value,
        offspring_return: detail.economics?.offspring_return,
    };

    return (
        <section className="donora-detail__card">
            <h3 className="donora-detail__section-title">Rendimiento económico</h3>
            <div className="donora-detail__economics">
                <Field label="Tu porcentaje">{fmtPct(detail.percent)}</Field>
                {DONOR_ECONOMIC_ROWS.map((row) => (
                    <Field key={row.key} label={row.label}>
                        {row.money ? fmtMoney(money[row.key]) : fmtNum(money[row.key])}
                    </Field>
                ))}
            </div>
        </section>
    );
}

// Producción SG «último-X» (Q-123): el último evento por feature (leche /
// pesaje / servicio). Solo se monta si el módulo viene en available_modules
// (hay ≥1 dato conciliado); con feature sin dato → "sin registros aún".
// NO confundir con data.production (reproductivo, StatsSection de arriba).
const SG_ICONS = { milk: IconMilk, scale: IconScale, syringe: IconSyringe };

function SgProductionCard({ sg }) {
    if (!sg) return null;

    return (
        <section className="donora-detail__card">
            <h3 className="donora-detail__section-title">Producción (Software Ganadero)</h3>
            <div className="donora-detail__sg">
                {SG_FEATURES.map((feature) => {
                    const Icon = SG_ICONS[feature.icon];
                    const last = sg[feature.key]?.last;
                    return (
                        <article key={feature.key} className="donora-detail__sg-feature">
                            <header className="donora-detail__sg-head">
                                <span className="donora-detail__sg-icon"><Icon /></span>
                                <span className="donora-detail__sg-label">{feature.label}</span>
                                {last?.date && (
                                    <span className="donora-detail__sg-date">{formatDateOnly(last.date)}</span>
                                )}
                            </header>

                            {last ? (
                                <dl className="donora-detail__sg-fields">
                                    {feature.fields.map((field) => {
                                        const value = last[field.key];
                                        if (value == null || value === '') return null;
                                        return (
                                            <div key={field.key} className="donora-detail__sg-field">
                                                <dt>{field.label}</dt>
                                                <dd>{field.fmt === 'num' ? fmtNum(value) : value}</dd>
                                            </div>
                                        );
                                    })}
                                </dl>
                            ) : (
                                <p className="donora-detail__muted">Sin registros aún.</p>
                            )}
                        </article>
                    );
                })}
            </div>
        </section>
    );
}

// «Liquidación mensual» (D-009): el reparto CONGELADO del socio — última
// liquidación (LIQUIDATION_ROWS: días, tarifa/día, pensión, tu % del reparto,
// tu reparto) + histórico por período. Tres conceptos que NO se mezclan:
// «Tu participación» (percent, co-propiedad — arriba), «Tu reparto del mes»
// (dinero real de la liquidación) y «Valor de tu participación» (stake_value,
// null hasta que staff cargue purchase_value). El front solo formatea.
const LIQ_FMT = { num: fmtNum, money: fmtMoney, pct: fmtPct };

// Curva del módulo económico (D-009): evolución mensual del reparto congelado.
// Dos series sobre un mismo eje (misma unidad, COP): «Tu reparto» (share_amount,
// serie de datos → área teal) y «Pensión» (pension_amount, línea de CONTEXTO →
// gris punteada). ASC por período (contrato). El backend congela los montos; el
// front solo formatea. El TrendChart común pone hover/tooltip/leyenda/tabla.
function LiquidationTrend({ history }) {
    const xLabels = history.map((h) => periodLabel(h.period));
    const series = [
        {
            key: 'share',
            label: 'Tu reparto',
            variant: 'area',
            values: history.map((h) => Number(h.my_share?.share_amount) || 0),
        },
        {
            key: 'pension',
            label: 'Pensión',
            variant: 'context',
            values: history.map((h) => Number(h.pension_amount) || 0),
        },
    ];

    return (
        <TrendChart
            xLabels={xLabels}
            series={series}
            format={fmtMoney}
            ariaLabel="Evolución mensual: tu reparto vs. pensión"
        />
    );
}

function LiquidationCard({ animalId }) {
    const { data: envelope } = useClientLiquidation(animalId);

    // Gate por módulo del sobre (economic → donor_owner). Sin módulo = N/A
    // para tu rol → la card no aparece; loading/error quedan silenciosos (el
    // bloque es opcional — patrón DonorsKpiBlock, overview.jsx).
    if (!envelopeModules(envelope).includes('economic')) return null;

    const item = (envelopeData(envelope).items ?? [])[0];
    const last = item?.last_liquidation;
    const history = item?.history ?? [];

    return (
        <section className="donora-detail__card">
            <h3 className="donora-detail__section-title">Liquidación mensual</h3>

            {!last && (
                <p className="donora-detail__muted">Aún sin liquidaciones para esta donadora.</p>
            )}

            {last && (
                <>
                    <div className="donora-detail__liq-last">
                        <div className="donora-detail__liq-cell">
                            <span className="donora-detail__liq-label">Período</span>
                            <span className="donora-detail__liq-value">
                                {periodLabel(last.period_year, last.period_month)}
                            </span>
                        </div>
                        {LIQUIDATION_ROWS.map((row) => {
                            const value = row.share ? last.my_share?.[row.key] : last[row.key];
                            if (value == null) return null;
                            return (
                                <div
                                    key={row.key}
                                    className={`donora-detail__liq-cell ${row.highlight ? 'donora-detail__liq-cell--highlight' : ''}`}
                                >
                                    <span className="donora-detail__liq-label">{row.label}</span>
                                    <span className="donora-detail__liq-value">{LIQ_FMT[row.fmt](value)}</span>
                                </div>
                            );
                        })}
                    </div>

                    {history.length > 0 && (
                        <div className="donora-detail__liq-history">
                            <h4 className="donora-detail__liq-history-title">
                                Evolución mensual
                            </h4>
                            <LiquidationTrend history={history} />
                        </div>
                    )}
                </>
            )}
        </section>
    );
}

// G4 — crías (descendencia). Distinto de offspring_return (E3), que es dinero.
function OffspringCard({ offspring }) {
    const items = offspring?.items || [];
    const count = offspring?.count ?? 0;

    return (
        <section className="donora-detail__card">
            <h3 className="donora-detail__section-title">Crías</h3>

            {count === 0 && items.length === 0 ? (
                <p className="donora-detail__muted">Sin crías registradas.</p>
            ) : (
                <>
                    <p className="donora-detail__offspring-summary">
                        {fmtNum(count)} cría{count === 1 ? '' : 's'}
                        {offspring?.registered != null
                            && ` (${fmtNum(offspring.registered)} registrada${offspring.registered === 1 ? '' : 's'})`}
                    </p>
                    {items.length > 0 && (
                        <ul className="donora-detail__offspring">
                            {items.map((calf) => (
                                <li key={calf.id} className="donora-detail__offspring-item">
                                    <span className="donora-detail__offspring-name">
                                        {calf.name || `Cría #${calf.id}`}
                                    </span>
                                    <span className="donora-detail__muted">
                                        Nº {calf.number}
                                        {calf.register && ` · ${calf.register}`}
                                        {calf.sex && ` · ${SEX_LABELS[calf.sex] || calf.sex}`}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </>
            )}
        </section>
    );
}

export default function DonoraDetail({ donorId }) {
    const { data: envelope, isLoading, isError, error } = useClientDonorDetail(
        donorId != null ? Number(donorId) : null,
    );

    if (isLoading) {
        return <div className="donora-detail__state">Cargando donadora...</div>;
    }

    if (isError) {
        // 404 = no co-poseída o inexistente (el backend no distingue, a
        // propósito) — estado amable, no error rojo.
        return (
            <section className="donora-detail">
                <BackLink />
                <p className={`donora-detail__state${error?.status === 404 ? '' : ' donora-detail__state--error'}`}>
                    {error?.status === 404
                        ? 'Esta donadora no está en tu cowhousing o no existe.'
                        : (error?.message || 'No se pudo cargar la donadora.')}
                </p>
            </section>
        );
    }

    const d = envelope?.data;
    if (!d) {
        return (
            <section className="donora-detail">
                <BackLink />
                <p className="donora-detail__state">Donadora no encontrada.</p>
            </section>
        );
    }

    return (
        <section className="donora-detail">
            <header className="donora-detail__header">
                <BackLink />
                <h2 className="donora-detail__title">
                    {d.entity?.name || `Donadora #${d.entity?.id ?? donorId}`}
                </h2>
                <div className="donora-detail__meta">
                    {d.entity?.number && <span>Nº {d.entity.number}</span>}
                    {d.entity?.register && <span>Registro {d.entity.register}</span>}
                    {d.entity?.sex && <span>{SEX_LABELS[d.entity.sex] || d.entity.sex}</span>}
                </div>
            </header>

            <EconomicsCard detail={d} />

            <Valorization economics={d.economics} currentValue={d.gross_value} />

            <section className="donora-detail__card">
                <h3 className="donora-detail__section-title">Indicadores reproductivos</h3>
                <StatsSection
                    production={d.production}
                    productionConfig={PRODUCTION_CARDS}
                    funnel={d.funnel}
                    rates={d.rates}
                    rateRows={RATE_ROWS}
                />
            </section>

            <OffspringCard offspring={d.offspring} />

            <LiquidationCard animalId={d.entity?.id ?? Number(donorId)} />

            {(envelope?.available_modules ?? []).includes('sg_production') && (
                <SgProductionCard sg={d.sg_production} />
            )}
        </section>
    );
}
